import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

/**
 * This object that represents a progressBar element
 * @typedef {Object} ProgressElement
 * @property {string} className - The class name of the progress element
 * @property {string} color - The color of the progress element
 * @property {number} fontSize - The font size of the text inside the progress element
 * @property {string} legend - The legend of the progress element
 * @property {number} percentage - The percentage value of the progress element
 * @property {boolean} showPercentage - Show the percentage value inside the progress element
 * @property {string} textColor - The color of the text inside the progress element
 * @property {number} value - The percentage value of the progress element
 *
 */

// Styles of the MultiProgress component
const styles = {
  /**
   * This function returns the styles of the progress container
   * @param {boolean} round - Round the progress bar
   * @param {string|number} height - The height of the progress bar
   */
  progressContainer: (round, height) => {
    const convertedHeight = typeof height === "string" ? height : height + "px";

    return {
      width: "100%",
      height: convertedHeight,
      position: "relative",
      borderRadius: round ? "400px" : "0",
      overflow: "hidden",
    };
  },

  /**
   * This function returns the styles of the progress background
   * @param {string} backgroundColor - The background color of the progress bar
   */
  progressBackground: (backgroundColor) => ({
    backgroundColor,
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  }),

  /**
   * This function returns the styles of the progress element
   * @param {Object} elementProps - The props of the progress element
   */
  progressElement: (elementProps) => {
    const { backgroundColor, percentage, transitionTime, round, textColor, fontSize } =
      elementProps;

    const roundString = round ? "40px" : "0";

    return {
      backgroundColor,
      width: `${percentage}%`,
      zIndex: 8,
      height: "100%",
      margin: 0,
      transition:
        "width " + transitionTime + "s ease-in-out, left " + transitionTime + "s ease-in-out",
      borderRadius: roundString,
      color: textColor,
      textAlign: "center",
      fontSize: fontSize ? fontSize : 8,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    };
  },
};

/**
 * The `LegendItem` component render a legend item inside the tooltip of the progress bar.
 * @component
 * @param {Object} props - The props of the LegendItem component
 * @param {string} props.color - The color of the legend item
 * @param {string} props.legend - The legend of the legend item
 * @param {string} props.value - The value of the legend item
 * @returns {JSX.Element} The LegendItem component
 */
const LegendItem = ({ color, legend, value }) => (
  <Box display="flex" alignItems="center">
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: color,
        marginRight: 3,
      }}
    />

    <Typography component="p" variant="body2" color="primary.contrast" textAlign="left">
      {legend}
    </Typography>

    <Typography
      component="p"
      variant="body2"
      color="primary.contrast"
      sx={{ marginLeft: 3, flex: "auto" }}
      textAlign="right"
    >
      {value}
    </Typography>
  </Box>
);

/**
 * Create an array of React elements based on the progress elements
 * @component
 * @param {Object} props - The props of the createElementArray function
 * @param {ProgressElement[]} props.elements - The array of progress elements
 * @param {number} props.gap - The gap between the progress elements in pixels
 * @param {boolean} props.roundElement - This Boolean value determines if the every progress element should be rounded
 * @param {number} props.transitionTime - The transition time of the progress bar
 * @returns {JSX.Element} The array of React elements
 */
function ProgressElements({ elements, gap, roundElement, transitionTime }) {
  const newElements = [];
  const legendItems = [];

  elements.forEach((element, i) => {
    newElements.push(
      <p
        key={i}
        aria-label={element.legend}
        style={{
          ...styles.progressElement({
            backgroundColor: element.color,
            percentage: element.percentage,
            transitionTime,
            round: roundElement,
            textColor: element.textColor,
            fontSize: element.fontSize,
          }),
        }}
        className={`AN-progress-element ${element.className}`}
      >
        {element.showPercentage && <span>{element.percentage}%</span>}
      </p>
    );

    legendItems.push(
      <LegendItem key={i} color={element.color} legend={element.legend} value={element.value} />
    );
  });

  return (
    <Tooltip placement="top" title={<div>{legendItems}</div>}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="start"
        width="100%"
        height="100%"
        columnGap={`${gap}px`}
      >
        {newElements}
      </Box>
    </Tooltip>
  );
}

/**
 * The `ANMultiProgressBar` component render a Progress Bar with multiple progress elements.
 * @component
 * @param {Object} props - The props of the ANMultiProgressBar component
 * @param {string} props.backgroundColor - The background color of the progress bar
 * @param {string} props.className - The class name of the progress bar
 * @param {ProgressElement[]} props.elements - Array of progress elements data
 * @param {number} props.gap - The gap between the progress elements in pixels
 * @param {number | string} props.height - The height of the progress bar
 * @param {boolean} props.round - Round the progress bar
 * @param {boolean} props.roundElement - Round the progress elements
 * @param {number} props.transitionTime - The transition time of the progress bar
 * @returns {JSX.Element} The MultiProgress component
 */
export default function ANMultiProgressBar({
  backgroundColor = "#ffffff",
  className,
  elements = [],
  gap = 0,
  height = 12,
  round = true,
  roundElement = false,
  transitionTime = 0.6,
}) {
  return (
    <section
      style={{ ...styles.progressContainer(round, height) }}
      role="progressbar"
      className={`AN-multi-progressbar ${className}`}
    >
      <div style={{ ...styles.progressBackground(backgroundColor, gap) }} />
      <ProgressElements
        elements={elements}
        gap={gap}
        transitionTime={transitionTime}
        roundElement={roundElement}
      />
    </section>
  );
}
